import { useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { gql, useQuery, useMutation } from "@apollo/client";
import Layout from "../../../../components/Layout";
import { Heading, Text } from "../../../../components/Type";
import Button from "../../../../components/Button";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import EmptyCard from "../../../../components/EmptyCard";
import ProfilePreview from "../../../../components/UserInterface/ProfilePreview";
import NotificationBanner from "../../../../components/NotificationBanner";
import EditProfileForm from "../../../../components/EditProfileForm";
import styles from "./index.module.scss";
import { formatPhoneNumber } from "../../../../utils/forms/formatting";

const GET_USER = gql`
  query User($id: ID!) {
    user(id: $id) {
      first_name
      last_name
      id
      phone
      phone_ext
      email
      adp_auth_num
      role {
        type
        name
      }
      locations {
        id
        name
        adp_auth_num
        address {
          address_1
          address_2
          city
          postal_code
          province
        }
      }
      organization {
        name
        address {
          address_1
          address_2
          city
          postal_code
          province
        }
      }
    }
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdateUser($input: updateUserInput) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const UserEditPage = () => {
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const { data, error, loading } = useQuery(GET_USER, {
    variables: {
      id: params.id,
    },
    fetchPolicy: "no-cache",
    skip: !params || !params.id,
  });
  const [org, setOrg] = useState(data?.user.organization || undefined);

  const role = data?.user?.role.type;
  const isAuth = (role === "authorizer" || role === "motion_support");

  const [updateProfile, { error: updateError }] = useMutation(UPDATE_PROFILE, {
    onCompleted: () => history.push(`/admin/user/${params.id}`),
  });
  const methods = useForm({ mode: "onChange" });

  const handleSubmit = async (values) => {
    if (!data.user?.id) throw new Error("Missing User ID");
    await updateProfile({
      variables: {
        input: {
          data: {
            phone: formatPhoneNumber(values.phone),
            adp_auth_num: values.adp_auth_num,
            phone_ext: values.phone_ext,
            ...(isAuth && {
              organization: org?.id || data?.user.organization?.id,
            }),
          },
          where: {
            id: data.user.id,
          },
        },
      },
    });
  }

  return (
    <Layout>
      <Helmet>
        <title>Edit Profile</title>
      </Helmet>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <Heading size="h3" className={styles.profileName}>
              Edit Profile
            </Heading>
          </div>
          <div className={styles.headerAction}>
            <Button
              primary
              submit
              icon={<ArrowRight />}
              onClick={methods.handleSubmit(handleSubmit)}
            >
              Save Profile
            </Button>
          </div>
        </div>
        {error && error.message}
        {loading ? (
          "loading..."
        ) : (
          <div className={styles.grid}>
            <div className={styles.sidebar}>
              <EmptyCard>
                <ProfilePreview size="medium" user={data.user} userRole />
                <div className={styles.email}>
                  <Text>Email</Text>
                  <Text className={styles.emailText}>{data.user.email}</Text>
                </div>
              </EmptyCard>
            </div>
            <div className={styles.content}>
              {updateError && (
                <NotificationBanner
                  error
                  content="Something went wrong, please try again."
                  className={styles.error}
                />
              )}
              <EditProfileForm
                methods={methods}
                data={data?.user}
                isAuth={isAuth}
                isSelectModalOpen={isSelectModalOpen}
                setIsSelectModalOpen={setIsSelectModalOpen}
                isCreateModalOpen={isCreateModalOpen}
                setIsCreateModalOpen={setIsCreateModalOpen}
                org={org}
                setOrg={setOrg}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserEditPage;
